import { useEffect, useId, useState } from 'react';
import type { FormEvent } from 'react';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/mk';
import {
  checkRange,
  needsPanel,
  presetOf,
  presetRange,
  type DayRangeValue,
  type PeriodPreset,
  type RangeProblem,
} from '../lib/ranges';
import { LockIcon } from './ui';

const PRESET_KEYS: Record<PeriodPreset, MessageKey> = {
  today: 'period.today',
  yesterday: 'period.yesterday',
  week: 'period.week',
  month: 'period.month',
  lastMonth: 'period.lastMonth',
};

const PROBLEM_KEYS: Record<RangeProblem, MessageKey> = {
  invalid: 'error.dateInvalid',
  order: 'error.rangeOrder',
  tooLong: 'error.rangeTooLong',
  future: 'error.future',
};

/**
 * Quick periods plus a from/to pair of date inputs. The range is checked here (valid days, order,
 * the endpoint's maximum length, not in the future) before anything is asked from the API.
 * `panelActive === false` marks the presets that reach past the free 7 days with a lock.
 */
export function DateRangeForm({
  value,
  today,
  maxDays,
  presets,
  panelActive,
  onChange,
}: {
  value: DayRangeValue;
  today: string;
  maxDays: number;
  presets: readonly PeriodPreset[];
  panelActive: boolean | null;
  onChange: (range: DayRangeValue) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const [draft, setDraft] = useState<DayRangeValue>(value);
  const [problem, setProblem] = useState<RangeProblem | null>(null);
  const active = presetOf(value, today, presets);

  useEffect(() => {
    setDraft(value);
    setProblem(null);
  }, [value.from, value.to]);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const p = checkRange(draft, maxDays, today);
    setProblem(p);
    if (!p) onChange(draft);
  };

  return (
    <div className="range">
      <div className="segmented" role="group" aria-label={t('period.label')}>
        {presets.map((p) => {
          const r = presetRange(p, today);
          const locked = panelActive === false && needsPanel(r, today);
          return (
            <button
              key={p}
              type="button"
              className="segmented-item"
              aria-pressed={active === p}
              onClick={() => onChange(r)}
              title={locked ? t('nav.locked') : undefined}
            >
              {t(PRESET_KEYS[p])}
              {locked ? (
                <span className="lock" aria-label={t('nav.locked')}>
                  <LockIcon size={12} />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <form className="range-form" onSubmit={submit} noValidate>
        <label htmlFor={`${id}-from`}>{t('period.from')}</label>
        <input
          id={`${id}-from`}
          type="date"
          value={draft.from}
          max={today}
          required
          onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? `${id}-problem` : undefined}
        />
        <label htmlFor={`${id}-to`}>{t('period.to')}</label>
        <input
          id={`${id}-to`}
          type="date"
          value={draft.to}
          max={today}
          required
          onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? `${id}-problem` : undefined}
        />
        <button type="submit" className="btn btn--secondary">
          {t('period.show')}
        </button>
      </form>
      {problem ? (
        <p id={`${id}-problem`} className="field-error" role="alert">
          {t(PROBLEM_KEYS[problem], { days: maxDays })}
        </p>
      ) : null}
    </div>
  );
}
